import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Save, History, Eye, FileText } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";

export default function AdminHandbook() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [versions, setVersions] = useState([]);
    const [current, setCurrent] = useState(null);
    const [draft, setDraft] = useState("");
    const [draftTitle, setDraftTitle] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [previewMode, setPreviewMode] = useState(false);
    const [savedMsg, setSavedMsg] = useState("");

    const load = async () => {
        try {
            const [{ data: list }, { data: cur }] = await Promise.all([
                api.get("/admin/handbook"),
                api.get("/admin/handbook/current"),
            ]);
            setVersions(list.versions);
            setCurrent(cur);
            setDraft(cur.content);
            setDraftTitle(cur.title);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const publish = async () => {
        setBusy(true);
        setErr("");
        setSavedMsg("");
        try {
            const { data } = await api.post("/admin/handbook", {
                content: draft,
                title: draftTitle,
            });
            setSavedMsg(`Published v${data.version}`);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!current) {
        return (
            <div className="page-shell">
                <header className="fade-up">
                    <p className="eyebrow">Handbook</p>
                    <h1 className="h1 mt-3">Crew handbook</h1>
                </header>
                <div className="brand-card mt-6 text-muted" data-testid="handbook-loading">
                    Loading handbook…
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <header className="fade-up flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="eyebrow">Handbook</p>
                    <h1 className="h1 mt-3" data-testid="handbook-title">
                        Crew handbook
                    </h1>
                    <p className="text-muted mt-2 max-w-[640px]">
                        Markdown content shown to every new crew/contractor during onboarding.
                        Publishing creates a new version — older acknowledgments stay tied to the
                        version they signed.
                    </p>
                </div>
                {current && (
                    <div className="brand-card" style={{ padding: "0.75rem 1.25rem" }}>
                        <p className="eyebrow" style={{ justifyContent: "flex-end" }}>
                            Current
                        </p>
                        <p className="font-display font-bold mt-1">v{current.version}</p>
                        <p className="text-xs text-muted">
                            Published {fmtDateTime(current.published_at)}
                        </p>
                    </div>
                )}
            </header>

            {err && (
                <div
                    className="brand-card mt-5"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="handbook-error"
                >
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 mt-6">
                <div className="brand-card fade-up" data-testid="handbook-editor">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <p className="eyebrow">Editor</p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                className={`btn ${previewMode ? "btn-ghost" : "btn-primary"}`}
                                onClick={() => setPreviewMode(false)}
                                data-testid="handbook-mode-edit"
                            >
                                <FileText size={14} />
                                Markdown
                            </button>
                            <button
                                type="button"
                                className={`btn ${previewMode ? "btn-primary" : "btn-ghost"}`}
                                onClick={() => setPreviewMode(true)}
                                data-testid="handbook-mode-preview"
                            >
                                <Eye size={14} />
                                Preview
                            </button>
                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="field-label">Title</label>
                        <input
                            className="field-input"
                            value={draftTitle}
                            onChange={(e) => setDraftTitle(e.target.value)}
                            disabled={!isAdmin}
                            data-testid="handbook-title-input"
                        />
                    </div>

                    {previewMode ? (
                        <div
                            className="prose prose-sm max-w-none mt-4 p-4 rounded-md"
                            style={{ background: "var(--brand-cream)", minHeight: 420 }}
                            data-testid="handbook-preview"
                        >
                            <ReactMarkdown>{draft}</ReactMarkdown>
                        </div>
                    ) : (
                        <textarea
                            className="field-textarea mt-4 font-mono"
                            style={{ minHeight: 480 }}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            disabled={!isAdmin}
                            data-testid="handbook-content-input"
                        />
                    )}

                    <div className="mt-5 flex items-center gap-3">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={publish}
                            disabled={!isAdmin || busy || draft === current?.content}
                            data-testid="handbook-publish"
                        >
                            <Save size={14} />
                            {busy ? "Publishing…" : "Publish new version"}
                        </button>
                        {savedMsg && (
                            <span className="text-sm" style={{ color: "var(--brand-success)" }}>
                                {savedMsg}
                            </span>
                        )}
                        {!isAdmin && (
                            <span className="text-xs text-muted">
                                Only admins can edit and publish.
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-5">
                    <div className="brand-card fade-up" data-testid="handbook-history">
                        <p className="eyebrow flex items-center gap-2">
                            <History size={14} />
                            Versions
                        </p>
                        <ul className="mt-3 flex flex-col gap-2 text-sm">
                            {versions.length === 0 && <li className="text-muted">No versions yet.</li>}
                            {versions.map((v) => (
                                <li
                                    key={v.id}
                                    className="p-2 rounded-md flex items-center justify-between"
                                    style={{
                                        background: v.is_current
                                            ? "rgba(210,5,28,0.06)"
                                            : "transparent",
                                    }}
                                >
                                    <span className="font-display font-bold">v{v.version}</span>
                                    <span className="text-xs text-muted">
                                        {fmtDateTime(v.published_at)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
